import { useRef, useEffect, useState } from 'react';
import { ApiFailure, api, tokens } from '../lib/api';
import type { PickupPointRow } from '../lib/types';
import { X } from 'lucide-react';
import { InlineIcon } from './InlineIcon';

const FIELD_LABEL: Record<string, string> = {
  externalRef: '外部单号',
  receiverName: '收件人',
  receiverPhone: '收件电话',
  receiverAddress: '收货地址',
  receiverPostcode: '邮编',
  itemDescription: '物品',
  packageCount: '件数',
  weightGram: '重量',
  codAmount: '代收货款',
  remark: '备注',
};

interface Preview {
  batchId: string;
  filename: string;
  headers: string[];
  mapping: Record<string, number>;
  missingRequired: string[];
  total: number;
  ok: number;
  warn: number;
  error: number;
  sample: Array<{
    rowNo: number;
    status: string;
    errors: string[];
    parsed: Record<string, unknown> | null;
  }>;
}

interface Result {
  created: number;
  skipped: number;
  failed: number;
}

interface RollbackResult {
  status: string;
  summary: { total: number; cancelled: number; failed: number; blocked: number };
  blocked: Array<{ orderNo: string; status: string; reason: string }>;
}

/**
 * 导入订单。
 *
 * ★ 预览这一步的全部价值在于**让人看一眼再决定**。
 *
 *   所以这里要显示的不是「解析成功」四个字,而是:
 *   哪一列被认成了哪个字段、有多少行有问题、问题出在第几行。
 *   看不到这些的话,预览和直接导没有区别。
 */
export function ImportDialog({
  points,
  permissions,
  onClose,
  onDone,
}: {
  points: PickupPointRow[];
  permissions: string[];
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [pickupPointId, setPickupPointId] = useState(points[0]?.id ?? '');

  /*
   * ★ 取货点默认值不能只在 useState 里取一次 —— 和建单弹窗同一个坑,
   *   这里是第二次。
   *
   *   弹窗开得比取货点列表加载得快的时候(页面刚刷新就点「导入」),
   *   初值是空的,等列表回来了状态也不会自己补上。于是预览时
   *   不带取货点,而**确认导入那一步整批失败**:
   *   界面上写的是「新建 0 单 · 失败 2 单」,逐行原因是
   *   「请选择取货点或填写取货地址」—— 而人明明看见下拉框里
   *   选着「本店」(value 对不上任何 option 时,浏览器就显示第一项)。
   *
   *   全量跑测试时慢一点就必然踩到:这两条用例一直在偶发红。
   */
  useEffect(() => {
    if (points.length === 0) return;
    setPickupPointId((cur) => (cur === '' ? points[0]!.id : cur));
  }, [points]);

  /*
   * ★ 光「等列表回来补上默认值」还不够 —— 人可能在那之前就把文件传了,
   *   而预览一旦不带取货点,批次里就永远没有它,确认那一步整批失败。
   *
   *   所以弹窗**自己去拿一份**:传进来的那份为空时(页面刚打开、
   *   或者调用方压根没传),自己补。拿到之前不让选文件。
   */
  const [ownPoints, setOwnPoints] = useState<PickupPointRow[]>(points);
  const [pointsReady, setPointsReady] = useState(points.length > 0);
  useEffect(() => {
    if (points.length > 0) {
      setOwnPoints(points);
      setPointsReady(true);
      return;
    }
    let cancelled = false;
    void api<PickupPointRow[]>('/pickup-points')
      .then((rows) => {
        if (cancelled) return;
        setOwnPoints(rows);
        setPickupPointId((cur) => (cur === '' ? (rows[0]?.id ?? '') : cur));
      })
      .catch(() => undefined)
      // 拿不到也要放行:没有取货点的租户靠地址列导入
      .finally(() => !cancelled && setPointsReady(true));
    return () => {
      cancelled = true;
    };
  }, [points]);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rolledBack, setRolledBack] = useState<RollbackResult | null>(null);
  const canRollback = permissions.includes('import:rollback');

  async function upload(file: File) {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      /*
       * ★ 传文件的这一刻**现确认一次取货点**。
       *
       *   前面那两道(等列表回来补默认值、没到就禁掉选择框)都只是
       *   减少窗口,而这一步是关死它:人手快、或者用例直接 setInputFiles,
       *   照样会在取货点到达之前走到这里 —— 而预览一旦不带取货点,
       *   批次里就永远没有它,确认那一步整批失败,
       *   界面只写「新建 0 单」,人完全不知道自己快了半秒。
       */
      let pointId = pickupPointId;
      if (!pointId) {
        const rows = await api<PickupPointRow[]>('/pickup-points').catch(() => []);
        pointId = rows[0]?.id ?? '';
        if (pointId) {
          setOwnPoints(rows);
          setPickupPointId(pointId);
        }
      }

      const fd = new FormData();
      fd.append('file', file);
      if (pointId) fd.append('pickupPointId', pointId);

      // 带文件的请求不能走 api()(它会设 Content-Type: application/json,
      // 那样 multipart 的 boundary 就丢了)
      const t = tokens.get();
      const res = await fetch('/api/v1/imports/orders/preview', {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t.accessToken}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: { message: string } } | null;
        throw new Error(body?.error?.message ?? '解析失败');
      }
      setPreview((await res.json()) as Preview);
    } catch (e) {
      setError(e instanceof Error ? e.message : '解析失败');
    } finally {
      setBusy(false);
    }
  }

  async function rollback() {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      setRolledBack(
        await api<RollbackResult>(`/imports/orders/${preview.batchId}/rollback`, {
          method: 'POST',
        }),
      );
      await onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : '撤销失败');
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!preview || busy) return;
    setBusy(true);
    setError(null);
    try {
      setResult(
        await api<Result>(`/imports/orders/${preview.batchId}/confirm`, { method: 'POST' }),
      );
      await onDone();
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : '导入失败');
    } finally {
      setBusy(false);
    }
  }

  const blocked = preview != null && preview.missingRequired.length > 0;

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="导入订单"
        className="w-full max-w-4xl max-h-[90dvh] overflow-y-auto rounded-2xl bg-white
                   dark:bg-slate-800 p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-baseline justify-between">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">导入订单</h3>
          <button onClick={onClose} aria-label="关闭" className="text-slate-400 text-xl">
            <InlineIcon icon={X} />
          </button>
        </div>
        <p className="mb-4 text-sm text-slate-500">
          支持 .xlsx 和 .csv。第一行是列名,系统会自动识别。
          <span className="text-slate-400">先预览,确认无误再导入。</span>
        </p>

        {error && (
          <div className="mb-3 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                          dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {/* ── 导入完成 ── */}
        {result && (
          <div className="rounded-xl border border-green-300 dark:border-green-900
                          bg-green-50 dark:bg-green-950/40 p-4">
            <div className="font-semibold text-green-800 dark:text-green-300">导入完成</div>
            <div className="mt-1 text-sm text-slate-700 dark:text-slate-300">
              新建 {result.created} 单
              {result.skipped > 0 && ` · 跳过 ${result.skipped} 单(单号已存在)`}
              {result.failed > 0 && (
                <span className="text-red-600 dark:text-red-400">
                  {' '}
                  · 失败 {result.failed} 单
                </span>
              )}
            </div>
            {/*
              ★ 撤销按钮就放在这一屏。
                「列映射选错了」这件事,人是在**看到导入结果的那一刻**
                才反应过来的 —— 等他关掉对话框去别处找撤销入口,
                那三百单已经在派了。
            */}
            {canRollback && !rolledBack && result.created > 0 && (
              <button
                onClick={() => void rollback()}
                disabled={busy}
                className="mt-3 ml-2 px-3 py-2 rounded-lg border border-red-300 dark:border-red-900
                           text-red-700 dark:text-red-400 text-sm disabled:opacity-40"
              >
                {busy ? '撤销中…' : `撤销这一批(${result.created} 单)`}
              </button>
            )}
            <button
              onClick={onClose}
              className="mt-3 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-medium"
            >
              完成
            </button>

            {rolledBack && (
              <div className="mt-3 rounded-lg bg-white dark:bg-slate-800 p-3 text-sm">
                <div className="font-medium text-slate-800 dark:text-slate-200">
                  已撤销 {rolledBack.summary.cancelled} 单
                </div>
                {rolledBack.blocked.length > 0 && (
                  <div className="mt-2">
                    {/*
                      ★ 撤不掉的逐条列出来。只给一个数字的话,
                        人得自己去订单列表里一个个找是哪几单。
                    */}
                    <div className="text-amber-700 dark:text-amber-400">
                      有 {rolledBack.blocked.length} 单撤不掉,要手工处理:
                    </div>
                    <ul className="mt-1 space-y-0.5 text-xs text-slate-600 dark:text-slate-400">
                      {rolledBack.blocked.map((b) => (
                        <li key={b.orderNo}>
                          <span className="font-mono">{b.orderNo}</span> —— {b.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── 选文件 ── */}
        {!preview && !result && (
          <div className="space-y-3">
            {ownPoints.length > 0 && (
              <label className="block">
                <span className="block mb-1 text-sm text-slate-600 dark:text-slate-400">
                  取货点(整批共用)
                </span>
                <select
                  aria-label="取货点"
                  value={pickupPointId}
                  onChange={(e) => setPickupPointId(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-slate-300 dark:border-slate-600
                             bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
                >
                  {ownPoints.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <input
              ref={fileRef}
              type="file"
              aria-label="选择文件"
              accept=".xlsx,.csv"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                /*
                 * ★ 取货点还没到就先存着,等到了再传。
                 *   不等的话预览会不带取货点发出去,而确认那一步整批失败 ——
                 *   人看到的是「新建 0 单」,却完全不知道自己快了半秒。
                 */
                if (f) void upload(f);
              }}
              disabled={!pointsReady}
              className="block w-full text-sm text-slate-600 dark:text-slate-400
                         file:mr-3 file:h-10 file:px-4 file:rounded-lg file:border-0
                         file:bg-brand-600 file:text-white file:text-sm file:font-medium"
            />
            {busy && <p className="text-sm text-slate-500">解析中…</p>}
          </div>
        )}

        {/* ── 预览 ── */}
        {preview && !result && (
          <div className="space-y-4">
            <div className="flex items-center gap-3 flex-wrap text-sm">
              <span className="text-slate-500">{preview.filename}</span>
              <span className="text-slate-900 dark:text-slate-100">共 {preview.total} 行</span>
              <span className="text-green-700 dark:text-green-400">可导 {preview.ok}</span>
              {preview.warn > 0 && (
                <span className="text-amber-700 dark:text-amber-400">警告 {preview.warn}</span>
              )}
              {preview.error > 0 && (
                <span className="text-red-700 dark:text-red-400">错误 {preview.error}</span>
              )}
            </div>

            {/* ★ 列映射必须显示。整表错位一行、电话列被认成金额,
                   都是从这里一眼看出来的 */}
            <section>
              <h4 className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                识别到的列
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(preview.mapping)
                  .filter(([f]) => FIELD_LABEL[f])
                  .map(([field, idx]) => (
                    <span
                      key={field}
                      className="px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-700 text-xs
                                 text-slate-700 dark:text-slate-300"
                    >
                      {preview.headers[idx as number]}
                      <span className="text-slate-400"> → </span>
                      {FIELD_LABEL[field]}
                    </span>
                  ))}
              </div>

              {blocked && (
                <div className="mt-2 rounded-lg bg-red-50 dark:bg-red-950 border border-red-200
                                dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                  缺少必填列:
                  {preview.missingRequired.map((f) => FIELD_LABEL[f] ?? f).join('、')}
                  。请在表里补上对应的列再传一次。
                </div>
              )}
            </section>

            <section>
              <h4 className="mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300">
                前 {preview.sample.length} 行
              </h4>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400">
                    <tr>
                      <th className="p-2 text-left font-medium w-14">行号</th>
                      <th className="p-2 text-left font-medium w-16">状态</th>
                      <th className="p-2 text-left font-medium">内容 / 问题</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.sample.map((r) => (
                      <tr
                        key={r.rowNo}
                        className={`border-t border-slate-100 dark:border-slate-700/50 ${
                          r.status === 'ERROR' ? 'bg-red-50/60 dark:bg-red-950/20' : ''
                        }`}
                      >
                        {/* ★ 行号和 Excel 里看到的一致 —— 否则用户找不到改哪行 */}
                        <td className="p-2 tabular-nums text-slate-500">{r.rowNo}</td>
                        <td className="p-2">
                          <span
                            className={`text-xs px-1.5 py-0.5 rounded ${
                              r.status === 'OK'
                                ? 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                                : r.status === 'WARN'
                                  ? 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-400'
                                  : 'bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-400'
                            }`}
                          >
                            {r.status === 'OK' ? '可导' : r.status === 'WARN' ? '警告' : '错误'}
                          </span>
                        </td>
                        <td className="p-2 text-slate-700 dark:text-slate-300">
                          {r.errors.length > 0 ? (
                            <span className={r.status === 'ERROR' ? 'text-red-700 dark:text-red-400' : ''}>
                              {r.errors.join('; ')}
                            </span>
                          ) : (
                            <span className="text-slate-500">
                              {String(r.parsed?.['receiverName'] ?? '')} ·{' '}
                              {String(r.parsed?.['receiverPhone'] ?? '')} ·{' '}
                              <span className="text-slate-400">
                                {String(r.parsed?.['receiverAddress'] ?? '')}
                              </span>
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.total > preview.sample.length && (
                <p className="mt-1 text-xs text-slate-400">
                  还有 {preview.total - preview.sample.length} 行没显示,导入后可在批次详情里查看
                </p>
              )}
            </section>

            <div className="flex gap-3">
              <button
                onClick={() => setPreview(null)}
                className="flex-1 h-10 rounded-lg border border-slate-300 dark:border-slate-600
                           text-slate-700 dark:text-slate-300"
              >
                换个文件
              </button>
              <button
                onClick={() => void confirm()}
                disabled={busy || blocked || preview.ok + preview.warn === 0}
                className="flex-1 h-10 rounded-lg bg-brand-600 text-white font-medium
                           disabled:opacity-40"
              >
                {busy
                  ? '导入中…'
                  : blocked
                    ? '缺必填列,无法导入'
                    : `导入 ${preview.ok + preview.warn} 单`}
              </button>
            </div>
            {preview.error > 0 && (
              // 有错误行时说清楚会发生什么 —— 别让人以为点了就全导进去了
              <p className="text-xs text-slate-500">
                {preview.error} 行有错误,不会被导入。修好之后可以只把这几行单独传一次。
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
